import type { SupportedLocale } from '../types';
import {
  ANALYTICS_STORAGE_KEYS,
  COOKIE_CONSENT_CHANGED_EVENT,
  hasAnalyticsConsent,
  type CookieConsentPreferences
} from './cookieConsent';
import { setupGoogleAnalyticsConsentListener, trackGoogleAnalyticsEvent } from './googleAnalytics';

const API_BASE_URL = import.meta.env.VITE_API_GATEWAY_URL || '/api';
const MAX_BATCH_SIZE = 20;
const FLUSH_DELAY_MS = 30000;

type AnalyticsMetadataScalar = string | number | boolean | null;
type AnalyticsMetadataArray = Array<string | number | boolean>;
export type AnalyticsMetadataValue = AnalyticsMetadataScalar | AnalyticsMetadataArray;

export type AnalyticsEventType =
  | 'page_view'
  | 'detail_view'
  | 'select_content'
  | 'open_route_setup'
  | 'open_route_setup_from_home'
  | 'open_route_setup_from_onboarding'
  | 'open_route_setup_from_codex'
  | 'open_species_from_home_pick'
  | 'open_planner_from_route_setup'
  | 'auth_login'
  | 'auth_logout'
  | 'auth_prompt_click'
  | 'onboarding_start'
  | 'onboarding_complete'
  | 'recommendation_view'
  | 'affiliate_click'
  | 'affiliate_click_from_route_setup'
  | 'complete_setup_view'
  | 'complete_setup_item_click'
  | 'complete_setup_affiliate_click'
  | 'save_route'
  | 'save_route_from_route_setup'
  | 'save_plan'
  | 'compare_saved_routes'
  | 'resume_saved_route'
  | 'resume_saved_plan'
  | 'save_favorite_species'
  | 'resume_favorite_species'
  | 'save_favorite_technique'
  | 'resume_favorite_technique'
  | 'save_favorite_location_type'
  | 'resume_favorite_location_type'
  | 'save_favorite_rig'
  | 'resume_favorite_rig';

export type AnalyticsPageCategory = 'home' | 'auth' | 'codex' | 'toolbox' | 'logbook' | 'onboarding' | 'planner' | 'unknown';
export type AnalyticsEntityType = 'species' | 'technique' | 'location_type' | 'rig' | 'setup_guide' | 'navigation' | 'recommendation';

interface AnalyticsAuthContext {
  isAuthenticated: boolean;
  idToken?: string | null;
  userId?: string | null;
  email?: string | null;
}

interface AnalyticsEventInput {
  eventType: AnalyticsEventType;
  route: string;
  pageCategory: AnalyticsPageCategory;
  locale: SupportedLocale;
  entityType?: AnalyticsEntityType;
  entityId?: string;
  entityName?: string;
  metadata?: Record<string, AnalyticsMetadataValue>;
}

interface AnalyticsEventPayload extends AnalyticsEventInput {
  clientEventId: string;
  occurredAt: string;
  anonymousId: string;
  sessionId: string;
  source: 'frontend-web';
  userId?: string;
}

const isBrowser = typeof window !== 'undefined';

const isLighthouseAudit = (): boolean => (
  typeof navigator !== 'undefined' && /Chrome-Lighthouse|Lighthouse/i.test(navigator.userAgent)
);

const createId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

const normalizeRoute = (route: string): string => {
  if (!route) {
    return '/';
  }

  if (route === '/') {
    return route;
  }

  const trimmed = route.endsWith('/') ? route.slice(0, -1) : route;
  return trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
};

const isIgnorableFlushError = (error: unknown, keepalive: boolean): boolean => {
  if (error instanceof DOMException && error.name === 'AbortError') {
    return true;
  }

  if (error instanceof TypeError) {
    const message = error.message.toLowerCase();
    const likelyNavigationFailure =
      message.includes('failed to fetch') ||
      message.includes('load failed') ||
      message.includes('networkerror');

    if (likelyNavigationFailure) {
      return keepalive || (typeof document !== 'undefined' && document.visibilityState === 'hidden');
    }
  }

  return false;
};

const sanitizeMetadata = (
  metadata?: Record<string, AnalyticsMetadataValue>
): Record<string, AnalyticsMetadataValue> | undefined => {
  if (!metadata) {
    return undefined;
  }

  const normalizedEntries: Array<[string, AnalyticsMetadataValue]> = [];

  for (const [key, value] of Object.entries(metadata)) {
    if (value === undefined) {
      continue;
    }

    if (Array.isArray(value)) {
      const sanitizedArray = value.filter(
        (item): item is string | number | boolean => ['string', 'number', 'boolean'].includes(typeof item)
      );

      if (sanitizedArray.length > 0) {
        normalizedEntries.push([key, sanitizedArray]);
      }

      continue;
    }

    if (value === null || ['string', 'number', 'boolean'].includes(typeof value)) {
      normalizedEntries.push([key, value]);
    }
  }

  if (normalizedEntries.length === 0) {
    return undefined;
  }

  return Object.fromEntries(normalizedEntries);
};

class AnalyticsService {
  private queue: AnalyticsEventPayload[] = [];
  private flushTimer: number | null = null;
  private isFlushing = false;
  private authContext: AnalyticsAuthContext = { isAuthenticated: false };
  private listenersAttached = false;

  constructor() {
    if (!isBrowser) {
      return;
    }

    if (hasAnalyticsConsent()) {
      this.enableTracking();
    }

    setupGoogleAnalyticsConsentListener();

    window.addEventListener(COOKIE_CONSENT_CHANGED_EVENT, ((event: CustomEvent<CookieConsentPreferences>) => {
      if (event.detail.analytics) {
        this.enableTracking();
      } else {
        this.disableTracking();
      }
    }) as EventListener);
  }

  private enableTracking(): void {
    if (!isBrowser) {
      return;
    }

    this.queue = this.readQueue();

    if (this.listenersAttached) {
      return;
    }

    this.listenersAttached = true;

    window.addEventListener('online', () => {
      void this.flush();
    });

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        void this.flush(true);
      }
    });
  }

  private disableTracking(): void {
    this.queue = [];
    if (this.flushTimer !== null && isBrowser) {
      window.clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }
  }

  setAuthContext(authContext: AnalyticsAuthContext): void {
    this.authContext = authContext;
  }

  trackPageView(input: Omit<AnalyticsEventInput, 'eventType'>): void {
    this.track({ ...input, eventType: 'page_view' });
  }

  trackDetailView(input: Omit<AnalyticsEventInput, 'eventType'>): void {
    this.track({ ...input, eventType: 'detail_view' });
  }

  trackSelectContent(input: Omit<AnalyticsEventInput, 'eventType'>): void {
    this.track({ ...input, eventType: 'select_content' });
  }

  trackRouteSetupEvent(
    eventType:
      | 'open_route_setup'
      | 'open_route_setup_from_home'
      | 'open_route_setup_from_onboarding'
      | 'open_route_setup_from_codex'
      | 'open_species_from_home_pick'
      | 'open_planner_from_route_setup'
      | 'affiliate_click_from_route_setup'
      | 'save_route_from_route_setup',
    input: Omit<AnalyticsEventInput, 'eventType'>
  ): void {
    this.track({ ...input, eventType });
  }

  trackAuthEvent(
    eventType: 'auth_login' | 'auth_logout' | 'auth_prompt_click',
    input: Omit<AnalyticsEventInput, 'eventType'>
  ): void {
    this.track({ ...input, eventType });
  }

  trackOnboardingEvent(
    eventType: 'onboarding_start' | 'onboarding_complete',
    input: Omit<AnalyticsEventInput, 'eventType'>
  ): void {
    this.track({ ...input, eventType });
  }

  trackRecommendationView(input: Omit<AnalyticsEventInput, 'eventType'>): void {
    this.track({ ...input, eventType: 'recommendation_view' });
  }

  trackAffiliateClick(input: Omit<AnalyticsEventInput, 'eventType'>): void {
    this.track({ ...input, eventType: 'affiliate_click' });
  }

  trackCompleteSetupEvent(
    eventType: 'complete_setup_view' | 'complete_setup_item_click' | 'complete_setup_affiliate_click',
    input: Omit<AnalyticsEventInput, 'eventType'>
  ): void {
    this.track({ ...input, eventType });
  }

  trackSaveEvent(
    eventType: 'save_route' | 'save_plan' | 'resume_saved_route' | 'resume_saved_plan',
    input: Omit<AnalyticsEventInput, 'eventType'>
  ): void {
    this.track({ ...input, eventType });
  }

  trackSavedRouteCompare(input: Omit<AnalyticsEventInput, 'eventType'>): void {
    this.track({ ...input, eventType: 'compare_saved_routes' });
  }

  trackFavoriteEvent(
    eventType: 'save_favorite_species' | 'resume_favorite_species' | 'save_favorite_technique' | 'resume_favorite_technique' | 'save_favorite_location_type' | 'resume_favorite_location_type' | 'save_favorite_rig' | 'resume_favorite_rig',
    input: Omit<AnalyticsEventInput, 'eventType'>
  ): void {
    this.track({ ...input, eventType });
  }

  private track(input: AnalyticsEventInput): void {
    if (!isBrowser || isLighthouseAudit() || !hasAnalyticsConsent()) {
      return;
    }

    const payload: AnalyticsEventPayload = {
      ...input,
      route: normalizeRoute(input.route),
      metadata: sanitizeMetadata(input.metadata),
      clientEventId: createId(),
      occurredAt: new Date().toISOString(),
      anonymousId: this.getAnonymousId(),
      sessionId: this.getSessionId(),
      source: 'frontend-web',
      ...(this.authContext.userId ? { userId: this.authContext.userId } : {})
    };

    this.queue.push(payload);
    trackGoogleAnalyticsEvent({
      eventType: input.eventType,
      route: payload.route,
      pageCategory: input.pageCategory,
      locale: input.locale,
      entityType: input.entityType,
      entityId: input.entityId,
      entityName: input.entityName,
      metadata: payload.metadata
    });
    this.persistQueue();
    this.scheduleFlush();
  }

  async flush(keepalive = false): Promise<void> {
    if (!isBrowser || isLighthouseAudit() || !hasAnalyticsConsent() || this.isFlushing || this.queue.length === 0) {
      return;
    }

    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      return;
    }

    const baseUrl = API_BASE_URL?.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL;
    if (!baseUrl) {
      return;
    }

    const batch = this.queue.slice(0, MAX_BATCH_SIZE);
    this.isFlushing = true;

    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json'
      };

      if (this.authContext.idToken) {
        headers.Authorization = `Bearer ${this.authContext.idToken}`;
      }

      const response = await fetch(`${baseUrl}/analytics/events`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ events: batch }),
        keepalive
      });

      if (!response.ok) {
        throw new Error(`Analytics ingestion failed with status ${response.status}`);
      }

      this.queue = this.queue.slice(batch.length);
      this.persistQueue();

      if (this.queue.length > 0) {
        window.setTimeout(() => {
          void this.flush();
        }, 0);
      }
    } catch (error) {
      if (isIgnorableFlushError(error, keepalive)) {
        return;
      }

      if (import.meta.env.DEV) {
        console.warn('Analytics flush failed', error);
      }
    } finally {
      this.isFlushing = false;
    }
  }

  private scheduleFlush(): void {
    if (!isBrowser) {
      return;
    }

    if (this.queue.length >= MAX_BATCH_SIZE) {
      void this.flush();
      return;
    }

    if (this.flushTimer !== null) {
      window.clearTimeout(this.flushTimer);
    }

    this.flushTimer = window.setTimeout(() => {
      this.flushTimer = null;
      void this.flush();
    }, FLUSH_DELAY_MS);
  }

  private persistQueue(): void {
    if (!isBrowser) {
      return;
    }

    window.localStorage.setItem(ANALYTICS_STORAGE_KEYS.queue, JSON.stringify(this.queue));
  }

  private readQueue(): AnalyticsEventPayload[] {
    if (!isBrowser) {
      return [];
    }

    try {
      const raw = window.localStorage.getItem(ANALYTICS_STORAGE_KEYS.queue);
      if (!raw) {
        return [];
      }

      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  private getAnonymousId(): string {
    if (!isBrowser) {
      return 'server';
    }

    const existing = window.localStorage.getItem(ANALYTICS_STORAGE_KEYS.anonymousId);
    if (existing) {
      return existing;
    }

    const created = createId();
    window.localStorage.setItem(ANALYTICS_STORAGE_KEYS.anonymousId, created);
    return created;
  }

  private getSessionId(): string {
    if (!isBrowser) {
      return 'server';
    }

    const existing = window.sessionStorage.getItem(ANALYTICS_STORAGE_KEYS.sessionId);
    if (existing) {
      return existing;
    }

    const created = createId();
    window.sessionStorage.setItem(ANALYTICS_STORAGE_KEYS.sessionId, created);
    return created;
  }
}

export const analyticsService = new AnalyticsService();
